import { Box, Typography, useTheme, Button, Grid, Card, CardContent, CircularProgress, Alert } from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'
import ReportCardBig from '../components/ReportCardBig'
import { formatMoney } from '../utils/global';
import ProductReport from '../components/ProductReport';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AttachMoney, Assessment } from '@mui/icons-material';
import { getReports } from '../services/admin/AdminServices';
import { ReportResponse, ReportFilters as ReportFiltersType } from '../utils/types';
import ReportFilters from '../components/ReportFilters';

export default function AdminReportContainer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReportFiltersType>({
    reportType: 'monthly',
    period: 'monthly',
    periodOffset: 0
  });

  const fetchReportData = useCallback(async (currentFilters: ReportFiltersType) => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      
      if (currentFilters.reportType === 'custom') {
        if (currentFilters.startDate) params.startDate = currentFilters.startDate;
        if (currentFilters.endDate) params.endDate = currentFilters.endDate;
      } else {
        params.period = currentFilters.period || currentFilters.reportType;
        params.periodOffset = currentFilters.periodOffset || 0;
      }

      if (currentFilters.jewelerId) params.jewelerId = currentFilters.jewelerId;
      if (currentFilters.branchId) params.branchId = currentFilters.branchId;

      const data = await getReports(params);
      setReportData(data);
    } catch (err) {
      setError('Rapor verileri yüklenirken hata oluştu');
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportData(filters);
  }, [fetchReportData, filters]);

  const handleFiltersChange = (newFilters: ReportFiltersType) => {
    setFilters(newFilters);
    fetchReportData(newFilters);
  };

  const handleExport = async (exportFilters: ReportFiltersType) => {
    // Export functionality can be implemented here
    console.log('Export with filters:', exportFilters);
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          sx={{
            fontSize: '30px',
            fontWeight: '400'
          }}
        >
          Raporlar
        </Typography>
        <Button
          variant="contained"
          startIcon={<TrendingUp />}
          onClick={() => navigate('/admin/detailed-reports')}
          sx={{ 
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark
            }
          }}
        >
          Detaylı Raporlar
        </Button>
      </Box>

      {/* Report Filters */}
      <ReportFilters
        onFiltersChange={handleFiltersChange}
        onExport={handleExport}
        loading={loading}
      />

      {/* Period Information */}
      {reportData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Rapor Dönemi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reportData.period.type === 'monthly' && 'Aylık Rapor'}
              {reportData.period.type === 'quarterly' && 'Çeyreklik Rapor'}
              {reportData.period.type === 'yearly' && 'Yıllık Rapor'}
              {' - '}
              {new Date(reportData.period.startDate).toLocaleDateString('tr-TR')} - {new Date(reportData.period.endDate).toLocaleDateString('tr-TR')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {reportData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCardBig
              label="Toplam Ciro"
              badgeColor="#1976d2"
              price={(reportData.totalRevenue / 100).toFixed(2)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCardBig
              label="Toplam Kar"
              badgeColor="#2e7d32"
              price={(reportData.totalProfit / 100).toFixed(2)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCardBig
              label="Toplam İşlem"
              badgeColor="#ed6c02"
              price={reportData.totalTransactions.toString()}
            />
          </Grid>
        </Grid>
      )}

      {/* Additional Metrics */}
      {reportData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6">Ortalama İşlem Değeri</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {formatMoney((reportData.averageTransactionValue / 100).toFixed(2))} TL
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment sx={{ mr: 1, color: theme.palette.success.main }} />
                  <Typography variant="h6">Kar Marjı</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {reportData.detailedMetrics.profitMargin.toFixed(2)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ mr: 1, color: theme.palette.info.main }} />
                  <Typography variant="h6">Net Kar</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {formatMoney(((reportData.totalProfit - reportData.totalLoss) / 100).toFixed(2))} TL
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Product Summary */}
      {reportData && reportData.productSummary && Object.keys(reportData.productSummary).length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Ürün Özeti
          </Typography>
          <ProductReport products={reportData.productSummary} />
        </Box>
      )}
    </Box>
  )
}
